<script lang="ts">
export default {
  name: 'ReportingHub',
};
</script>

<script setup lang="ts">
import { computed, inject, onMounted, ref, watch, provide, watchEffect } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { reportingService, ReportingTableauType } from '@/services/ReportingService';
import { TableauDashboard } from '@/types/TableauDashboard';
import { Store } from 'vuex';
import { AppState } from '@/store';
import { Document, DocumentType } from '@/types/v2/Document';
import { Factory, Wrench, ChartBarIcon, FileTextIcon } from 'lucide-vue-next';
import type { ReportingType } from '@/services/ReportingService';
import { documentService } from '@/services/v2/DocumentService';

// -----
// data
// -----

const tableauVizHeight = ref('');
const resourceTypeHeader = ref<HTMLElement | null>(null);
const loading = ref<boolean>(true);
const reportingTypes = ref<ReportingType[]>([]);
const dashboards = ref<TableauDashboard[]>([]);
const documents = ref<Document[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const tableauTypes = ref<ReportingTableauType[]>([]);
const selectedCategoryType = ref<string>('');
const store = inject<Store<AppState>>('store');
const router = useRouter();
const route = useRoute();
const activeFilters = ref<string[]>([]);

// ---------
// watchers
// ---------
watchEffect(() => {
  // Go back to the dashboard if the user navigates to the reporting hub
  if (route.path === '/reporting') {
    selectedCategoryType.value = '';
  }
});

watch(
  () => route.params.categoryId,
  async (category) => {
    if (category) {
      selectedCategoryType.value = category as string;
    }
  },
);

// -----------
// lifecycles
// -----------

onMounted(async () => {
  loading.value = true;
  console.log('categoryId from route:', route.params.categoryId);

  // Fetch reporting types from the service
  await fetchReportingTypes();

  if (route.params.categoryId) {
    selectedCategoryType.value = route.params.categoryId as string;
  }

  await refresh();
});

// ----------
// functions
// ----------

async function refresh() {
  loading.value = true;
  computeTableauHeight();
  await Promise.all([fetchReportingTableauDashboards(), fetchDocuments(), fetchDocumentTypes(), fetchTableauTypes()]);

  loading.value = false;
}

async function fetchReportingTypes() {
  try {
    reportingTypes.value = await reportingService.getReportingTypes();
  } catch (error) {
    console.error('Error fetching reporting types:', error);
    console.error('Using default reporting types');
    // If the service fails, create default types
    reportingTypes.value = [
      { name: 'Production', description: 'Production related reports' },
      { name: 'Operations', description: 'Operations related reports' },
      { name: 'Data Quality', description: 'Data quality reports' },
    ];
  }
}

async function fetchReportingTableauDashboards() {
  dashboards.value = await reportingService.getReportingTableauDashboards();
}

async function fetchDocuments() {
  documents.value = await reportingService.getReportingDocuments();
}

async function fetchDocumentTypes() {
  documentTypes.value = (await documentService.getDocumentTypes()).filter((t) => t.domainName === 'Reporting') || [];
}

async function fetchTableauTypes() {
  tableauTypes.value = (await reportingService.getTableauTypes()) || [];
}

function computeTableauHeight() {
  const calculatedHeight =
    window.innerHeight -
    (store?.state.resizerRegistry['header'].height ?? 0) -
    (resourceTypeHeader.value?.offsetHeight ?? 170);
  tableauVizHeight.value = `${calculatedHeight}px`;
}

function handleSelectResourceType(typeName: string) {
  selectedCategoryType.value = typeName;
  router.push({ path: `/reporting/category/${encodeURIComponent(typeName)}` });
  activeFilters.value = [];
}

function toggleFilter(filter: string) {
  if (activeFilters.value.includes(filter)) {
    activeFilters.value = activeFilters.value.filter((f) => f !== filter);
  } else {
    activeFilters.value.push(filter);
  }
}

function clearFilters() {
  activeFilters.value = [];
}

function updated() {
  refresh();
}

// ---------
// computed
// ---------

const filteredDashboards = computed<TableauDashboard[]>(() => {
  const dashboardsByCategoryType = dashboards.value.filter((dashboard) => {
    return dashboard.reportingType === selectedCategoryType.value;
  });

  if (activeFilters.value.length > 0) {
    return dashboardsByCategoryType.filter(
      (dashboard) => dashboard.accountId !== null && activeFilters.value.includes(dashboard.tableauType),
    );
  }

  return dashboardsByCategoryType;
});

const filteredDocuments = computed<Document[]>(() => {
  const docsByCategoryType = documents.value.filter((doc) => {
    return doc.reportingType === selectedCategoryType.value;
  });

  if (activeFilters.value.length > 0) {
    return docsByCategoryType.filter((document) => activeFilters.value.includes(document.documentTypeName));
  }

  return docsByCategoryType;
});

const selectedCategoryName = computed<string>(() => {
  return selectedCategoryType.value;
});

const documentsForCategory = computed(() => {
  return documents.value.filter((document) => document.accountId !== null);
});

const dashboardsForCategory = computed(() => {
  return dashboards.value.filter((dashboard) => dashboard.accountId !== null);
});

const visibleDocumentFilters = computed(() => {
  return documentTypes.value.filter((type) =>
    documentsForCategory.value.some((doc) => doc.documentTypeName === type.name),
  );
});

const visibleTableauFilters = computed(() => {
  return tableauTypes.value.filter((type) => dashboardsForCategory.value.some((dashboard) => dashboard.tableauType === type.tableauTypeName));
});

// --------
// provide
// --------
provide('selectedCategoryType', selectedCategoryType);
</script>

<template>
  <div v-if="!selectedCategoryType" class="tw:m-auto tw:flex tw:flex-col tw:items-center tw:gap-4 tw:pt-8">
    <div class="tw:flex tw:flex-col tw:items-center">
      <h2>Reporting Hub</h2>
      <div>Please choose from one of the categories below</div>
    </div>

    <div class="tw:flex tw:w-120 tw:flex-wrap tw:justify-center tw:gap-4 tw:pt-2">
      <template v-for="reportingType in reportingTypes" :key="reportingType.name">
        <div class="tw:group tw:tooltip tw:pointer-events-auto tw:z-20 tw:cursor-pointer">
          <div
            class="tw:flex tw:h-24 tw:w-36 tw:flex-col tw:justify-center tw:rounded-lg tw:border tw:border-solid tw:border-blue-200 tw:bg-white tw:shadow-md tw:transition-all tw:duration-150 tw:group-hover:border-blue-200 tw:group-hover:bg-blue-50 tw:group-hover:shadow-none"
            @click="handleSelectResourceType(reportingType.name)">
            <div class="tw:text-black-500 tw:flex tw:flex-col tw:items-center tw:gap-2 tw:self-center tw:p-2">
              <Factory v-if="reportingType.name === 'Production'" class="tw:text-gray-400" />
              <Wrench v-if="reportingType.name === 'Operations'" class="tw:text-gray-400" />
              <ChartBarIcon v-if="reportingType.name === 'Data Quality'" class="tw:text-gray-400" />
              <FileTextIcon
                v-if="!['Production', 'Operations', 'Data Quality'].includes(reportingType.name)"
                class="tw:text-gray-400" />

              <h2 class="tw:text-center tw:text-sm tw:font-medium">{{ reportingType.name }}</h2>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>

  <div v-if="selectedCategoryType" class="tw:relative tw:flex tw:h-full">
    <aside class="tw:w-60 tw:border-r tw:border-gray-300 tw:bg-gray-100 tw:p-2">
      <nav class="tw:sticky tw:top-20 tw:w-60 tw:p-2">
        <h2 class="tw:text-xl tw:text-primary">Categories</h2>

        <div class="tw:mt-4">
          <div
            v-for="(reportingType, i) in reportingTypes"
            :key="reportingType.name"
            :aria-label="reportingType.name"
            :tabindex="i"
            role="button"
            class="tw:m-1 tw:mr-3 tw:rounded-md tw:border tw:border-gray-100 tw:p-1 tw:px-2 tw:text-lg tw:transition-all tw:select-none tw:hover:border-gray-300 tw:hover:bg-gray-50"
            :class="{
              'tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30': selectedCategoryType === reportingType.name,
            }"
            @click="handleSelectResourceType(reportingType.name)">
            {{ reportingType.name }}
          </div>
        </div>

        <!-- filters -->
        <div>
          <div class="tw:mt-4 tw:flex tw:items-center tw:gap-2">
            <h2 class="tw:text-xl tw:text-primary">Filter by Type</h2>
            <button
              v-if="activeFilters.length > 0"
              class="tw:btn tw:rounded-lg tw:bg-specialty-teal/30 tw:btn-xs"
              @click="clearFilters">
              Clear All
            </button>
          </div>
          <div class="tw:mt-4 tw:flex tw:flex-col tw:gap-1 tw:p-3">
            <div class="tw:flex tw:flex-wrap tw:justify-center tw:gap-2">
              <!-- Document Filters -->
              <div
                v-for="(type, i) in visibleDocumentFilters"
                :key="type.id"
                :aria-label="type.name"
                :tabindex="i"
                data-testid="doc-filter"
                role="button"
                class="tw:text-md tw:btn tw:rounded tw:shadow-md tw:transition-all tw:duration-75 tw:hover:bg-gray-50"
                :class="{
                  'tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30': activeFilters.includes(type.name),
                }"
                @click="toggleFilter(type.name)">
                {{ type.name }}
              </div>

              <!-- Tableau Type Filters -->
              <div
                v-for="(type, i) in visibleTableauFilters"
                :key="type.tableauTypeId"
                :aria-label="type.tableauTypeName"
                :tabindex="i"
                role="button"
                class="filter-button tw:text-md tw:border-gray-200 tw:shadow-md tw:transition-all tw:duration-75 tw:hover:bg-gray-50"
                :class="{
                  'tw:bg-specialty-teal/30 tw:hover:bg-specialty-teal/30': activeFilters.includes(type.tableauTypeName),
                }"
                @click="toggleFilter(type.tableauTypeName)">
                {{ type.tableauTypeName }}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </aside>

    <router-view
      :loading="loading"
      :dashboards="filteredDashboards"
      :document-types="documentTypes"
      :tableau-types="tableauTypes"
      :documents="filteredDocuments"
      :selectedCategoryName="selectedCategoryName"
      @updated="updated" />
  </div>
</template>
